/* ══════════════════════════════════════════════════════
   RIDEX CITY — INVESTOR ROI & COST ENGINE
   
   Cost Breakdown per Unit:
   - Bike Cost: ₹75,000
   - IoT Cost: ₹5,000
   - Integration Cost: ₹8,000
   - Battery Cost: ₹5,000
   - Add-Ons: ₹6,000
   Total Base Unit Value: ₹99,000

   Plans:
   - 1 Unit: ₹99,000
   - 10 Units: ₹9,00,000 (₹90,000 / unit)
   - 25 Units: ₹21,25,000 (₹85,000 / unit)
   - Custom: Tiered pricing (1-9 @ 99k, 10-24 @ 90k, 25+ @ 85k)

   Returns & Payback:
   - ₹3,000 / month per unit for all plans
   - Approx Payback / Contract Period: 72 Months (6 Years)
   - 6-Year Total Return (ROI): 1 Unit: 218% | 10 Units: 240% | 25 Units: 254%
   - Annualised Return: 1 Unit: 36% | 10 Units: 40% | 25 Units: 42%
══════════════════════════════════════════════════════ */

export const MONTHLY_RETURN_PER_UNIT = 3000;
export const ANNUAL_RETURN_PER_UNIT = MONTHLY_RETURN_PER_UNIT * 12; // ₹36,000

export const UNIT_COST_BREAKDOWN = {
  vehicle: { label: 'Vehicle Unit Cost', baseRate: 75000, icon: 'TwoWheeler' },
  iot: { label: 'IoT & GPS Telematics', baseRate: 5000, icon: 'Sensors' },
  integration: { label: 'Platform & Tech Integration', baseRate: 8000, icon: 'Hub' },
  battery: { label: 'Battery (Pack / Deposit)', baseRate: 5000, icon: 'BatteryChargingFull' },
  addOns: { label: 'Fleet Add-Ons & Safety', baseRate: 6000, icon: 'Security' },
};

export const FIXED_PLANS_CONFIG = [
  {
    id: 1,
    name: 'Starter Fleet',
    units: 1,
    ratePerUnit: 99000,
    amount: 99000,
    badge: 'Popular for Beginners',
    highlight: false,
    sixYearReturnPct: '218%',
    annualisedReturnPct: '36%',
  },
  {
    id: 2,
    name: 'Growth Fleet',
    units: 10,
    ratePerUnit: 90000,
    amount: 900000,
    badge: 'Save ₹90,000 (10 Units)',
    highlight: true,
    sixYearReturnPct: '240%',
    annualisedReturnPct: '40%',
  },
  {
    id: 3,
    name: 'Enterprise Fleet',
    units: 25,
    ratePerUnit: 85000,
    amount: 2125000,
    badge: 'Save ₹3,50,000 (25 Units)',
    highlight: false,
    sixYearReturnPct: '254%',
    annualisedReturnPct: '42%',
  },
];

export function getUnitPrice(units) {
  const u = Math.max(1, Math.round(Number(units) || 1));
  if (u >= 25) return 85000;
  if (u >= 10) return 90000;
  return 99000;
}

export function get6YearRoiPct(units) {
  const u = Math.max(1, Math.round(Number(units) || 1));
  if (u >= 25) return 254;
  if (u >= 10) return 240;
  return 218;
}

export function formatINR(n) {
  const v = Math.round(Number(n) || 0);
  return '₹' + new Intl.NumberFormat('en-IN').format(v);
}

export function computePlanDetails(unitsInput) {
  const units = Math.max(1, Math.round(Number(unitsInput) || 1));
  const ratePerUnit = getUnitPrice(units);
  const totalInvestment = units * ratePerUnit;
  const standardCostAtBase = units * 99000;
  const savings = Math.max(0, standardCostAtBase - totalInvestment);

  const monthlyReturn = units * MONTHLY_RETURN_PER_UNIT;
  const annualReturn = monthlyReturn * 12;
  const return36Months = monthlyReturn * 36;
  const return60Months = monthlyReturn * 60;
  const return72Months = monthlyReturn * 72; // 6 Years (72 Months)
  const annualRoiPct = (annualReturn / totalInvestment) * 100;
  const total3YrRoiPct = (return36Months / totalInvestment) * 100;
  const total6YrRoiPct = get6YearRoiPct(units);

  // Breakdown scale factor
  const costScale = ratePerUnit / 99000;

  const costBreakdownRows = [
    { item: 'Bike Cost', rate: 75000, amount: 75000 * units * costScale, desc: 'Commercial grade electric 2-wheeler' },
    { item: 'IoT Cost', rate: 5000, amount: 5000 * units * costScale, desc: 'Smart GPS telematics, real-time tracking, anti-theft' },
    { item: 'Integration Cost', rate: 8000, amount: 8000 * units * costScale, desc: 'RideX City cloud operating system & platform onboarding' },
    { item: 'Battery Cost', rate: 5000, amount: 5000 * units * costScale, desc: 'Certified lithium-ion swap pack with warranty' },
    { item: 'Add-ons', rate: 6000, amount: 6000 * units * costScale, desc: 'Heavy-duty carrier, crash safety guard, helmet lock' },
  ];

  return {
    units,
    ratePerUnit,
    totalInvestment,
    savings,
    monthlyReturn,
    annualReturn,
    return36Months,
    return60Months,
    return72Months,
    annualRoiPct,
    total3YrRoiPct,
    total6YrRoiPct,
    costBreakdownRows,
  };
}

// Backward compatibility alias
export const BASE_UNIT = 99000;
export function computeFleetEconomics(amount) {
  // Derive units from amount
  const safeAmount = Math.max(Number(amount) || 99000, 99000);
  let units = 1;
  if (safeAmount >= 2125000) {
    units = Math.round(safeAmount / 85000);
  } else if (safeAmount >= 900000) {
    units = Math.round(safeAmount / 900000 * 10);
  } else {
    units = Math.max(1, Math.round(safeAmount / 99000));
  }
  return computePlanDetails(units);
}
